import { DESTINATION_LEVELS } from "app/constants";
import type {
	HotelAccommodationDescription,
	HotelAddress,
	HotelGeography,
	HotelGeographyLevel,
	HotelSections,
	HotelUsefulInformations,
} from "app/features/hotel/hotelDescription/types";

type HotelOnlyReview = {
	id?: string;
	numReviews?: number;
	rating?: number;
	ratingUrl?: string;
};

type HotelOnlyGeography = HotelGeography & {
	geometry?: { type?: string; coordinates?: number[] };
};

type HotelOnlyAccommodationDescription = {
	address?: HotelAddress;
	facilityGroups?: unknown[];
	geography?: HotelOnlyGeography;
	included?: string[];
	notIncluded?: string[];
	photos?: { url: string }[];
	reviews?: { tripadvisor?: HotelOnlyReview; holidaycheck?: HotelOnlyReview };
	sections?: HotelSections;
	sourceDescription?: string;
	summary?: string;
	usefulInformations?: HotelUsefulInformations;
};

type HotelOnlyAccommodation = {
	id: number;
	name: string;
	rating: number;
	description?: HotelOnlyAccommodationDescription;
};

export type HotelOnlyAccommodationResponse = {
	accommodation?: HotelOnlyAccommodation;
	distributions?: unknown;
};

const getLevelLabel = (levels: HotelGeographyLevel[] | undefined, level: string): string =>
	levels?.find(item => item.level === level)?.label ?? "";

const getGpsFromGeometry = (geometry: HotelOnlyGeography["geometry"]) => {
	const [lon, lat] = geometry?.coordinates ?? [];
	return { lat: lat ?? 0, lon: lon ?? 0 };
};

/**
 * Maps the nested hotel-only accommodation payload (`/hotelonly/accommodations/:id`)
 * into the flat `HotelAccommodationDescription` shape consumed by the shared hotel page.
 */
export const mapHotelOnlyAccommodation = (
	response: HotelOnlyAccommodationResponse
): HotelAccommodationDescription => {
	const accommodation = response?.accommodation;

	if (!accommodation) {
		return { id: "", status: "TECHNICAL_FAILURE" };
	}

	const { description } = accommodation;
	const levels = description?.geography?.levels;
	const resort = getLevelLabel(levels, DESTINATION_LEVELS.RESORT);
	const region = getLevelLabel(levels, DESTINATION_LEVELS.REGION);
	const tripadvisor = description?.reviews?.tripadvisor;

	return {
		id: String(accommodation.id),
		status: "SUCCESS",
		name: accommodation.name,
		category: accommodation.rating,
		gps: getGpsFromGeometry(description?.geography?.geometry),
		geography: description?.geography ?? {},
		region,
		resort,
		location: [resort, region].filter(Boolean).join(", "),
		tripadvisor: {
			id: tripadvisor?.id ?? "",
			rating: tripadvisor?.rating ?? 0,
			num_reviews: tripadvisor?.numReviews ?? 0,
			ratingURL: tripadvisor?.ratingUrl ?? "",
		},
		photos: description?.photos,
		summary: description?.summary ?? "",
		pointsOfInterest: [],
		included: description?.included,
		notIncluded: description?.notIncluded,
		address: description?.address,
		usefulInformations: description?.usefulInformations,
		sections: description?.sections,
		facilityGroups: description?.facilityGroups,
		sourceDescription: description?.sourceDescription,
	};
};
